import { type Accessor, createEffect, createSignal, type Setter } from 'solid-js'

export type DebouncedSignal<T> = [value: Accessor<T>, debounced: Accessor<T>, setValue: Setter<T>]

/**
 * Señal "cruda" + señal "debounced" separadas por un delay.
 *
 * Antes el patrón `clearTimeout + setTimeout(300)` sobre dos señales estaba
 * copiado en 11 listados (clientes, usuarios, empresas, cotizaciones...). Aquí
 * vive una sola vez: se escribe en la primera señal y la segunda se actualiza
 * `delay` ms después de dejar de teclear.
 */
export function useDebouncedSignal<T>(initial: T, delay = 300): DebouncedSignal<T> {
  const [value, setValue] = createSignal<T>(initial)
  const [debounced, setDebounced] = createSignal<T>(initial)

  let timeout: ReturnType<typeof setTimeout> | undefined
  createEffect(() => {
    const next = value()
    clearTimeout(timeout)
    timeout = setTimeout(() => setDebounced(() => next), delay)
  })

  return [value, debounced, setValue]
}
